import { useCallback, useEffect, useMemo, useReducer, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { errorService } from '@ascleo/client/services/error.service'
import {
  addDaysToIsoDate,
  addMonthsToIsoDate,
  containsDateInclusive,
  dayOfIsoDate,
  epochMillisOfUtcInstant,
  firstDayOfIsoMonth,
  formatIsoDateForLocale,
  formatUtcInstantAsLocalIsoDate,
  formatUtcInstantAsLocalTime,
  lastDayOfIsoMonth,
  localHourOfUtcInstant,
  localIsoDateEndAsUtcInstant,
  localIsoDateStartAsUtcInstant,
  practiceTimeZone,
  systemClock,
  todayIsoDate
} from '@ascleo/types/date-time-validity'
import { KioskStep } from '../../../../../components/kiosk-step'
import { useSchedulerSend, useSchedulerSelector, useBookingQuestionnaireClient } from '../context'
import { BookingOfferChanged } from '../../../../../lib/rpc/booking-questionnaire.client'
import { docCirrusRpcClient } from '../../../../../lib/rpc/doc-cirrus.client'
import { useAudio } from '../../../../../lib/use-audio'
import {
  QuickSlotsStrip,
  SlotPickerPanel,
  SlotSelectionEmptyView,
  SlotSelectionErrorView,
  SlotSelectionLoadingView
} from './slot-selection-panels'
import {
  SLOT_LOAD_ERROR_MESSAGES,
  getTimePeriod,
  initialSlotLoadState,
  shortDateLabel,
  slotLoadReducer,
  type CalendarDay,
  type SelectableSlot,
  type SlotWithTime,
  type TimePeriod,
  type TimeSlot
} from './slot-selection-model'

function sortedCopy<T>(
  items: Iterable<T>,
  compare: (left: T, right: T) => number
): T[] {
  const sorted: T[] = []
  for (const item of items) {
    const insertAt = sorted.findIndex((existing) => compare(item, existing) < 0)
    if (insertAt === -1) {
      sorted.push(item)
    } else {
      sorted.splice(insertAt, 0, item)
    }
  }
  return sorted
}

/**
 * Slot selection step — handoff "Wann passt es Ihnen?" layout.
 *
 * Quick-pick strip + a two-pane picker: a compact month calendar and the
 * selected day's free times grouped by time of day (only periods with free
 * times; no filter chips, KO-61/62). An empty month replaces the calendar
 * with a plain message and month navigation (KO-64). Choosing a time (quick-pick or a period column)
 * commits immediately via SELECT_SLOT and advances to the confirmation page —
 * there is no separate confirm step. Back navigation is handled by the footer.
 *
 * Sizing is scaled up for large kiosk displays (bigger type + touch targets,
 * full-width layout).
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <SlotSelectionStep />
 * ```
 */
export function SlotSelectionStep() {
  const { t, i18n } = useTranslation()
  const send = useSchedulerSend()
  const questionnaireClient = useBookingQuestionnaireClient()
  const enabledQuestionnaire = useSchedulerSelector((s) => s.context.questionnaireEnabled)
  const appointmentTypeId = useSchedulerSelector((s) => s.context.appointmentTypeId)
  const slotRequestGeneration = useRef(0)
  const invalidateSlotRequest = useCallback(() => { ++slotRequestGeneration.current }, [])
  const [slotLoadState, dispatchSlotLoad] = useReducer(
    slotLoadReducer,
    initialSlotLoadState,
  )
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const slots = slotLoadState.slots

  useAudio('scheduler-slotSelection')

  const dateLabelLocale = (i18n.resolvedLanguage || i18n.language || 'de').split('-')[0] ?? 'de'
  const today = useMemo(() => todayIsoDate(systemClock, practiceTimeZone), [])
  const [viewMonth, setViewMonth] = useState(() => firstDayOfIsoMonth(today))

  const fetchSlots = useCallback(
    async (monthStart: string) => {
      if (!appointmentTypeId) {
        dispatchSlotLoad({ type: 'missing-appointment-type' })
        return
      }

      const ownedGeneration = ++slotRequestGeneration.current
      dispatchSlotLoad({ type: 'loading-started' })

      try {
        const effectiveStart = containsDateInclusive(today, monthStart, lastDayOfIsoMonth(monthStart))
          ? today
          : monthStart
        const endDate = lastDayOfIsoMonth(monthStart)

        const startDate = localIsoDateStartAsUtcInstant(effectiveStart)
        const endInstant = localIsoDateEndAsUtcInstant(endDate)
        if (enabledQuestionnaire === true && questionnaireClient === null) throw new Error('Questionnaire session is required')
        const response = enabledQuestionnaire === true && questionnaireClient !== null
          ? await questionnaireClient.slots(appointmentTypeId, startDate, endInstant)
          : await docCirrusRpcClient.frontdesk.getAvailableTimeSlots({ appointmentTypeId, startDate, endDate: endInstant })

        if (ownedGeneration !== slotRequestGeneration.current) return
        const mappedSlots: TimeSlot[] = [...response].map((slot) => ({
          id: slot.id,
          calendarId: slot.calendarId,
          start: slot.startTime,
          end: slot.endTime,
          appointmentTypeId: slot.appointmentTypeId
        }))

        dispatchSlotLoad({ type: 'slots-loaded', slots: mappedSlots })
      } catch (err) {
        if (ownedGeneration !== slotRequestGeneration.current) return
        if (err instanceof BookingOfferChanged) { send({ type: 'QUESTIONNAIRE_OFFER_CHANGED', route: err.route }); return }
        dispatchSlotLoad({ type: 'slots-load-failed' })
        // The step shows the failure inline; the toast channel stays quiet (KO-01, KO-02).
        errorService.report(enabledQuestionnaire === true ? new Error('Questionnaire slot read failed') : err, {
          layer: 'rpc',
          source: 'frontdesk.scheduler.time-slots.fetch',
          userMessage: i18n.t(SLOT_LOAD_ERROR_MESSAGES.fetchFailed),
          suppressToast: true
        })
      }
    },
    [appointmentTypeId, today, i18n, enabledQuestionnaire, questionnaireClient, send]
  )

  useEffect(function runFetchSlotsEffect() {
    void fetchSlots(viewMonth)
    return function ignoreSupersededSlotRequest() { invalidateSlotRequest() }
  }, [viewMonth, fetchSlots, invalidateSlotRequest])

  const canGoPreviousMonth = useMemo(() => viewMonth > firstDayOfIsoMonth(today), [viewMonth, today])

  const handlePreviousMonth = useCallback(() => {
    if (canGoPreviousMonth) {
      setViewMonth((prev) => firstDayOfIsoMonth(addMonthsToIsoDate(prev, -1)))
      setSelectedDate(null)
    }
  }, [canGoPreviousMonth])

  const handleNextMonth = useCallback(() => {
    setViewMonth((prev) => firstDayOfIsoMonth(addMonthsToIsoDate(prev, 1)))
    setSelectedDate(null)
  }, [])

  const groupedByDate = useMemo(() => {
    const grouped: Record<string, SlotWithTime[]> = {}
    for (const slot of slots) {
      const date = formatUtcInstantAsLocalIsoDate(slot.start)
      const hour = localHourOfUtcInstant(slot.start)
      const period = getTimePeriod(hour)

      grouped[date] ??= []
      grouped[date].push({
        id: slot.id,
        calendarId: slot.calendarId,
        time: formatUtcInstantAsLocalTime(slot.start),
        start: slot.start,
        end: slot.end,
        period
      })
    }
    return grouped
  }, [slots])

  const periodsAvailableByDate = useMemo(() => {
    const periods: Record<string, Set<TimePeriod>> = {}
    for (const [date, dateSlots] of Object.entries(groupedByDate)) {
      periods[date] = new Set(dateSlots.map((slot) => slot.period))
    }
    return periods
  }, [groupedByDate])

  const filteredGroupedByDate = groupedByDate

  const slotCountByDate = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const [date, dateSlots] of Object.entries(filteredGroupedByDate)) {
      counts[date] = dateSlots.length
    }
    return counts
  }, [filteredGroupedByDate])

  const firstAvailableDate = useMemo(() => {
    const dates = sortedCopy(
      Object.keys(filteredGroupedByDate).filter((date) => date >= today),
      (left, right) => left.localeCompare(right)
    )
    return dates[0] ?? null
  }, [filteredGroupedByDate, today])

  const effectiveSelectedDate = selectedDate ?? firstAvailableDate

  const quickSlots = useMemo(() => {
    const slotsWithDates = Object.entries(filteredGroupedByDate)
      .flatMap(([date, dateSlots]) =>
        dateSlots.map((slot) => ({
          ...slot,
          date,
          dateFormatted: shortDateLabel(date, dateLabelLocale)
        }))
      )
    return sortedCopy(
      slotsWithDates,
      (left, right) => epochMillisOfUtcInstant(left.start) - epochMillisOfUtcInstant(right.start)
    ).slice(0, 3)
  }, [filteredGroupedByDate, dateLabelLocale])

  const calendarDays = useMemo(() => {
    const days: CalendarDay[] = []
    const monthStart = firstDayOfIsoMonth(viewMonth)
    const monthEnd = lastDayOfIsoMonth(viewMonth)

    for (let dateStr = monthStart; dateStr <= monthEnd; dateStr = addDaysToIsoDate(dateStr, 1)) {
      const isPast = dateStr < today
      days.push({
        date: dateStr,
        dayNum: dayOfIsoDate(dateStr),
        isPast,
        isToday: dateStr === today,
        slotCount: isPast ? 0 : slotCountByDate[dateStr] || 0,
        periodsAvailable: isPast
          ? new Set<TimePeriod>()
          : periodsAvailableByDate[dateStr] || new Set<TimePeriod>()
      })
    }
    return days
  }, [viewMonth, today, slotCountByDate, periodsAvailableByDate])

  const slotsByPeriod = useMemo(() => {
    const by: Record<TimePeriod, SlotWithTime[]> = {
      morning: [],
      midday: [],
      afternoon: [],
      evening: []
    }
    if (!effectiveSelectedDate) return by

    for (const slot of filteredGroupedByDate[effectiveSelectedDate] ?? []) {
      by[slot.period].push(slot)
    }
    return by
  }, [filteredGroupedByDate, effectiveSelectedDate])

  const handleSelectDay = (date: string) => {
    setSelectedDate(date)
  }

  const handleSelectSlot = useCallback(
    (slot: SelectableSlot) => {
      send({
        type: 'SELECT_SLOT',
        slotId: slot.id,
        slotCalendarId: slot.calendarId,
        slotStart: slot.start,
        slotEnd: slot.end
      })
    },
    [send]
  )

  const dayLabel = effectiveSelectedDate
    ? formatIsoDateForLocale(effectiveSelectedDate, dateLabelLocale, { weekday: 'long', day: 'numeric', month: 'long' })
    : ''
  const loadErrorMessage =
    slotLoadState.status === 'error' ? t(SLOT_LOAD_ERROR_MESSAGES[slotLoadState.reason]) : null

  if (slotLoadState.status === 'loading') {
    return <SlotSelectionLoadingView />
  }

  if (loadErrorMessage) {
    return (
      <SlotSelectionErrorView
        message={loadErrorMessage}
        onRetry={() => fetchSlots(viewMonth)}
      />
    )
  }

  return (
    <KioskStep
      width="wide"
      title={t('self-service.scheduler.slot-selection.title')}
      lead={t('self-service.scheduler.slot-selection.subtitle')}
    >
      {slots.length === 0 ? (
        <SlotSelectionEmptyView
          monthLabel={formatIsoDateForLocale(viewMonth, dateLabelLocale, { month: 'long', year: 'numeric' })}
          canGoPreviousMonth={canGoPreviousMonth}
          onNextMonth={handleNextMonth}
          onPreviousMonth={handlePreviousMonth}
        />
      ) : (
        <>
          <QuickSlotsStrip
            quickSlots={quickSlots}
            onSelectSlot={handleSelectSlot}
          />
          <SlotPickerPanel
            dayLabel={dayLabel}
            viewMonth={viewMonth}
            dateLabelLocale={dateLabelLocale}
            calendarDays={calendarDays}
            effectiveSelectedDate={effectiveSelectedDate}
            canGoPreviousMonth={canGoPreviousMonth}
            slotsByPeriod={slotsByPeriod}
            onPreviousMonth={handlePreviousMonth}
            onNextMonth={handleNextMonth}
            onSelectDay={handleSelectDay}
            onSelectSlot={handleSelectSlot}
          />
        </>
      )}
    </KioskStep>
  )
}
